import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import supplierService from '../modules/suppliers/services/supplierService';
import inventoryService from '../modules/inventory/services/inventoryService';

interface TestResult {
  endpoint: string;
  status: 'pending' | 'success' | 'error';
  message: string;
  data?: any;
}

export const BackendConnectionTest: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const runTests = async () => {
    setIsRunning(true);
    setTestResults([]);

    const tests: Array<() => Promise<TestResult>> = [
      // Test supplier endpoints
      async () => {
        try {
          const result = await supplierService.getSuppliers({
            page: 1,
            pageSize: 5
          });
          return {
            endpoint: 'GET /api/suppliers/list',
            status: 'success',
            message: `Retrieved ${result.suppliers.length} suppliers`,
            data: result
          };
        } catch (error: any) {
          return {
            endpoint: 'GET /api/suppliers/list',
            status: 'error',
            message: error.message || 'Request failed'
          };
        }
      },

      async () => {
        try {
          const result = await supplierService.getSupplierMetrics();
          return {
            endpoint: 'GET /api/suppliers/metrics',
            status: 'success',
            message: 'Supplier metrics retrieved successfully',
            data: result
          };
        } catch (error: any) {
          return {
            endpoint: 'GET /api/suppliers/metrics',
            status: 'error',
            message: error.message || 'Request failed'
          };
        }
      },

      // Test inventory endpoints
      async () => {
        try {
          const result = await inventoryService.getInventoryItems({
            page: 1,
            pageSize: 5
          });
          return {
            endpoint: 'GET /api/inventory/items',
            status: 'success',
            message: `Retrieved ${result.items.length} inventory items`,
            data: result
          };
        } catch (error: any) {
          return {
            endpoint: 'GET /api/inventory/items',
            status: 'error',
            message: error.message || 'Request failed'
          };
        }
      },

      async () => {
        try {
          const result = await inventoryService.getInventoryMetrics();
          return {
            endpoint: 'GET /api/inventory/metrics',
            status: 'success',
            message: 'Inventory metrics retrieved successfully',
            data: result
          };
        } catch (error: any) {
          return {
            endpoint: 'GET /api/inventory/metrics',
            status: 'error',
            message: error.message || 'Request failed'
          };
        }
      }
    ];

    for (const test of tests) {
      const result = await test();
      setTestResults(prev => [...prev, result]);
      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsRunning(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      default: return '⏳';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Backend Connection Test
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Test the connection to your backend API endpoints to ensure they're working correctly.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={runTests}
          disabled={isRunning}
          startIcon={isRunning ? <CircularProgress size={20} /> : null}
        >
          {isRunning ? 'Running Tests...' : 'Run Connection Tests'}
        </Button>
      </Box>

      {testResults.length > 0 && (
        <Grid container spacing={2}>
          {testResults.map((result, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <span>{getStatusIcon(result.status)}</span>
                    <Typography variant="h6" component="div">
                      {result.endpoint}
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={result.status.toUpperCase()}
                    color={getStatusColor(result.status) as any}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary">
                    {result.message}
                  </Typography>

                  {result.data && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Response Data:
                      </Typography>
                      <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {testResults.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Test Summary:</strong> {testResults.filter(r => r.status === 'success').length} of {testResults.length} endpoints are working correctly.
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};
